import { useEffect, useRef, useState, useCallback } from 'react'
import { Button } from '../ui/button'
import { ChevronLeft, ChevronRight, List, X, Loader2, Sun, Moon, Coffee, Maximize2, Minimize2, Settings, Minus, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../../lib/api'

// Theme types
type ReaderTheme = 'light' | 'dark' | 'sepia'

interface ThemeConfig {
  name: string
  icon: typeof Sun
  bg: string
  text: string
  link: string
}

// Import foliate-js view module
import '../../lib/foliate-js/view.js'

// Extend HTMLElementTagNameMap for TypeScript
declare global {
  interface HTMLElementTagNameMap {
    'foliate-view': FoliateViewElement
  }
}

interface FoliateViewElement extends HTMLElement {
  open(file: string | File | Blob): Promise<void>
  close(): void
  goTo(target: string | number): Promise<void>
  goToFraction(fraction: number): Promise<void>
  prev(): Promise<void>
  next(): Promise<void>
  goLeft(): Promise<void>
  goRight(): Promise<void>
  init(options: { lastLocation?: string; showTextStart?: boolean }): Promise<void>
  renderer?: {
    setAttribute(name: string, value: string): void
    setStyles?(css: string): void
  }
  book?: {
    metadata?: {
      title?: string | Record<string, string>
      author?: string | Array<string | { name: string | Record<string, string> }>
      language?: string
    }
    toc?: TocItem[]
    dir?: 'rtl' | 'ltr'
  }
  getSectionFractions(): number[]
}

interface TocItem {
  label: string
  href: string
  subitems?: TocItem[]
}

interface RelocateDetail {
  fraction: number
  location?: { current: number }
  tocItem?: { href: string; label: string }
  pageItem?: { label: string }
  cfi?: string
}

interface FoliateViewerProps {
  url: string
  fileFormat?: string // 'epub' or 'fb2'
  className?: string
  // Preview mode props
  isPreview?: boolean
  previewStartPercent?: number
  previewMaxPercent?: number
  onPreviewEnd?: () => void
  // Progress tracking
  initialProgress?: number  // Initial progress percent (0-100)
  initialPosition?: string | null  // Initial CFI position
  onProgressChange?: (progress: number, position: string | null) => void
  // Sync props for side-by-side view
  onLocationChange?: (cfi: string, percent: number) => void
  syncLocation?: string
  // Control props
  showControls?: boolean  // Show header bar with title, theme, TOC, close
  showNavigation?: boolean  // Show prev/next navigation arrows (default: true)
  navAtEdges?: boolean     // Always position nav buttons at screen edges (for embedded/preview mode)
  onClose?: () => void
  // Book metadata display
  bookLanguage?: string  // Language code/name to display in header
  bookLevel?: string     // CEFR level (A1-C2) to display in header
  // User preferences (from server)
  initialFontSize?: number  // Initial font size from user profile (80-150)
  initialTheme?: 'light' | 'dark' | 'sepia'  // Initial theme from user profile
}

// Theme configurations
const THEMES: Record<ReaderTheme, ThemeConfig> = {
  light: {
    name: 'Light',
    icon: Sun,
    bg: '#ffffff',
    text: '#1a1a1a',
    link: '#0066cc'
  },
  dark: {
    name: 'Dark',
    icon: Moon,
    bg: '#1a1a1a',
    text: '#e8e8e8',
    link: '#6db3f2'
  },
  sepia: {
    name: 'Sepia',
    icon: Coffee,
    bg: '#f4ecd8',
    text: '#5c4b37',
    link: '#8b4513'
  }
}

// CSS for the reader content with theme and font size support
const getReaderCSS = (theme: ReaderTheme, fontSize: number = 100) => {
  const config = THEMES[theme]
  return `
  @namespace epub "http://www.idpf.org/2007/ops";
  html {
    background-color: ${config.bg} !important;
    color: ${config.text} !important;
    font-size: ${fontSize}% !important;
  }
  body {
    background-color: ${config.bg} !important;
    color: ${config.text} !important;
  }
  a:link, a:visited {
    color: ${config.link} !important;
  }
  p, li, blockquote, dd {
    line-height: 1.6;
    text-align: start;
    -webkit-hyphens: manual;
    hyphens: manual;
    hanging-punctuation: allow-end last;
    widows: 2;
  }
  [align="left"] { text-align: left; }
  [align="right"] { text-align: right; }
  [align="center"] { text-align: center; }
  [align="justify"] { text-align: justify; }
  pre {
    white-space: pre-wrap !important;
  }
  aside[epub|type~="endnote"],
  aside[epub|type~="footnote"],
  aside[epub|type~="note"],
  aside[epub|type~="rearnote"] {
    display: none;
  }
  /* Override any inline styles for better theme support */
  * {
    background-color: inherit;
  }
`
}

// Helper to format book metadata
const formatLanguageMap = (x: string | Record<string, string> | undefined): string => {
  if (!x) return ''
  if (typeof x === 'string') return x
  const keys = Object.keys(x)
  return keys.length > 0 ? x[keys[0]] : ''
}

export function FoliateViewer({
  url,
  fileFormat = 'epub',
  className = '',
  isPreview = false,
  previewStartPercent = 0,
  previewMaxPercent = 20,
  onPreviewEnd,
  initialProgress,
  initialPosition,
  onProgressChange,
  onLocationChange,
  showControls = true,
  showNavigation = true,
  navAtEdges = false,
  onClose,
  bookLanguage,
  bookLevel,
  initialFontSize,
  initialTheme
}: FoliateViewerProps) {
  const { t } = useTranslation('common')
  const containerRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<FoliateViewElement | null>(null)
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPercent, setCurrentPercent] = useState(0)
  const [showToc, setShowToc] = useState(false)
  const [toc, setToc] = useState<TocItem[]>([])
  const [bookTitle, setBookTitle] = useState('')
  const [previewEnded, setPreviewEnded] = useState(false)
  const [theme, setTheme] = useState<ReaderTheme>(() => {
    // Use initial theme from props (server) or fallback to localStorage
    if (initialTheme) return initialTheme
    const saved = localStorage.getItem('foliate-reader-theme')
    return (saved as ReaderTheme) || 'light'
  })
  const [fontSize, setFontSize] = useState<number>(() => {
    // Use initial font size from props (server) or fallback to localStorage
    if (initialFontSize) return initialFontSize
    const saved = localStorage.getItem('foliate-reader-font-size')
    return saved ? parseInt(saved, 10) : 100
  })
  const [showSettings, setShowSettings] = useState(false)
  const settingsRef = useRef<HTMLDivElement>(null)
  const settingsButtonRef = useRef<HTMLButtonElement>(null)
  const [isWideMode, setIsWideMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('foliate-reader-wide-mode')
    return saved === 'true'
  })
  const [isDesktop, setIsDesktop] = useState<boolean>(false)
  const [hasSpaceForOutsideNav, setHasSpaceForOutsideNav] = useState<boolean>(false)

  // Detect desktop screen size and check if there's enough space for nav buttons outside content
  // max-w-3xl = 768px, nav buttons need ~56px on each side, so need at least 768 + 112 = 880px
  // But with some padding, let's use ~900px as minimum for comfortable outside nav positioning
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      setIsDesktop(width >= 1024)
      // Check if there's enough space for nav buttons outside the max-w-3xl content
      // 768px (content) + 56px * 2 (buttons) + 32px (padding) = ~912px minimum
      setHasSpaceForOutsideNav(width >= 920)
    }
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  // Initialize foliate-view
  useEffect(() => {
    if (!containerRef.current || !url) return

    const initBook = async () => {
      try {
        setLoading(true)
        setError(null)
        setPreviewEnded(false)

        // Fetch the book file as Blob
        const token = localStorage.getItem('auth_token')
        const response = await fetch(url, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
        
        if (!response.ok) {
          throw new Error(`Failed to fetch book: ${response.status}`)
        }
        
        const blob = await response.blob()
        
        // Create a File object with proper name and MIME type for format detection
        // foliate-js detects format by filename extension or MIME type
        const format = fileFormat.toLowerCase()
        const fileName = format === 'fb2' ? 'book.fb2' : 'book.epub'
        const mimeType = format === 'fb2' ? 'application/x-fictionbook+xml' : 'application/epub+zip'
        const file = new File([blob], fileName, { type: mimeType })

        // Create foliate-view element
        const view = document.createElement('foliate-view') as FoliateViewElement
        viewRef.current = view
        
        // Style the view element
        view.style.width = '100%'
        view.style.height = '100%'
        view.style.display = 'block'

        // Add to container
        containerRef.current!.appendChild(view)

        // Listen for relocate events
        view.addEventListener('relocate', ((e: CustomEvent<RelocateDetail>) => {
          const { fraction, cfi } = e.detail
          const percent = fraction * 100
          setCurrentPercent(percent)

          // Notify parent of location change (for sync)
          if (onLocationChange && cfi) {
            onLocationChange(cfi, percent)
          }

          // Check preview limits
          if (isPreview && percent >= previewStartPercent + previewMaxPercent) {
            setPreviewEnded(true)
            if (onPreviewEnd) {
              onPreviewEnd()
            }
          }
        }) as EventListener)

        // Track progress changes (only when navigating forward in non-preview mode)
        let lastSavedPercent = initialProgress ?? 0
        view.addEventListener('relocate', ((e: CustomEvent<RelocateDetail>) => {
          if (isPreview) return // Don't track progress in preview mode
          
          const { fraction, cfi } = e.detail
          const percent = fraction * 100
          
          // Save progress when user moves forward (any amount)
          // The parent component handles debouncing to prevent excessive API calls
          if (onProgressChange && percent > lastSavedPercent) {
            lastSavedPercent = percent
            onProgressChange(percent, cfi ?? null)
          }
        }) as EventListener)

        // Listen for load events
        view.addEventListener('load', () => {
          // Could add document-level event handlers here if needed
        })

        // Open the book
        await view.open(file)

        // Get book metadata
        const book = view.book
        if (book) {
          setBookTitle(formatLanguageMap(book.metadata?.title) || '')
          setToc(book.toc || [])
        }

        // Set styles with current theme and font size
        view.renderer?.setStyles?.(getReaderCSS(theme, fontSize))

        // Set paginated flow
        view.renderer?.setAttribute('flow', 'paginated')

        // Initialize display - restore position if available
        if (!isPreview && initialPosition) {
          // Restore exact position using CFI
          try {
            await view.goTo(initialPosition)
          } catch (e) {
            console.warn('Failed to restore CFI position, falling back to progress:', e)
            if (initialProgress && initialProgress > 0) {
              await view.goToFraction(initialProgress / 100)
            } else {
              await view.init({ showTextStart: true })
            }
          }
        } else if (!isPreview && initialProgress && initialProgress > 0) {
          // Restore progress by percentage
          await view.goToFraction(initialProgress / 100)
        } else if (isPreview && previewStartPercent > 0) {
          await view.goToFraction(previewStartPercent / 100)
        } else {
          await view.init({ showTextStart: true })
        }

        setLoading(false)
      } catch (err) {
        console.error('Failed to load epub with Foliate:', err)
        setError(t('reader.loadError', 'Failed to load book'))
        setLoading(false)
      }
    }

    initBook()

    // Cleanup
    return () => {
      if (viewRef.current) {
        viewRef.current.close()
        viewRef.current.remove()
        viewRef.current = null
      }
    }
  }, [url, fileFormat, isPreview, previewStartPercent, previewMaxPercent, onPreviewEnd, onLocationChange, onProgressChange, initialProgress, initialPosition, t])

  // Update theme and font size when they change
  useEffect(() => {
    if (viewRef.current?.renderer?.setStyles) {
      viewRef.current.renderer.setStyles(getReaderCSS(theme, fontSize))
    }
    // Save preferences to localStorage as fallback
    localStorage.setItem('foliate-reader-theme', theme)
    localStorage.setItem('foliate-reader-font-size', String(fontSize))
  }, [theme, fontSize])

  // Save settings to server (debounced)
  const saveSettingsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveSettingsToServer = useCallback((newTheme: ReaderTheme, newFontSize: number) => {
    // Clear any pending save
    if (saveSettingsTimeoutRef.current) {
      clearTimeout(saveSettingsTimeoutRef.current)
    }
    // Debounce save to prevent excessive API calls
    saveSettingsTimeoutRef.current = setTimeout(() => {
      apiClient.preferences.updateUserPreferences({
        reader_theme: newTheme,
        reader_font_size: newFontSize
      }).catch(err => console.error('Failed to save reader settings:', err))
    }, 1000)
  }, [])

  // Cleanup settings save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveSettingsTimeoutRef.current) {
        clearTimeout(saveSettingsTimeoutRef.current)
      }
    }
  }, [])

  // Cycle through themes and save to server
  const cycleTheme = useCallback(() => {
    setTheme(current => {
      const themes: ReaderTheme[] = ['light', 'dark', 'sepia']
      const currentIndex = themes.indexOf(current)
      const newTheme = themes[(currentIndex + 1) % themes.length]
      saveSettingsToServer(newTheme, fontSize)
      return newTheme
    })
  }, [fontSize, saveSettingsToServer])

  // Font size handlers
  const increaseFontSize = useCallback(() => {
    setFontSize(current => {
      const newSize = Math.min(150, current + 10)
      saveSettingsToServer(theme, newSize)
      return newSize
    })
  }, [theme, saveSettingsToServer])

  const decreaseFontSize = useCallback(() => {
    setFontSize(current => {
      const newSize = Math.max(80, current - 10)
      saveSettingsToServer(theme, newSize)
      return newSize
    })
  }, [theme, saveSettingsToServer])

  // Close settings popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showSettings &&
        settingsRef.current &&
        !settingsRef.current.contains(e.target as Node) &&
        settingsButtonRef.current &&
        !settingsButtonRef.current.contains(e.target as Node)
      ) {
        setShowSettings(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showSettings])

  // Toggle wide/narrow mode
  const toggleWideMode = useCallback(() => {
    setIsWideMode(current => {
      const newValue = !current
      localStorage.setItem('foliate-reader-wide-mode', String(newValue))
      return newValue
    })
  }, [])

  // Navigation handlers
  const goNext = useCallback(() => {
    if (!viewRef.current || previewEnded) return
    
    // Check if we're at preview limit before navigating
    if (isPreview && currentPercent >= previewStartPercent + previewMaxPercent - 1) {
      setPreviewEnded(true)
      if (onPreviewEnd) {
        onPreviewEnd()
      }
      return
    }
    
    viewRef.current.next()
  }, [previewEnded, isPreview, currentPercent, previewStartPercent, previewMaxPercent, onPreviewEnd])

  const goPrev = useCallback(() => {
    if (!viewRef.current) return
    
    // In preview mode, don't go before start
    if (isPreview && previewStartPercent > 0 && currentPercent <= previewStartPercent + 1) {
      return
    }
    
    viewRef.current.prev()
  }, [isPreview, previewStartPercent, currentPercent])

  const goToTocItem = useCallback((href: string) => {
    if (!viewRef.current) return
    viewRef.current.goTo(href)
    setShowToc(false)
  }, [])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === 'l') {
        goNext()
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp' || e.key === 'h') {
        goPrev()
      } else if (e.key === 'Escape' && onClose) {
        onClose()
      } else if (e.key === 't' || e.key === 'T') {
        cycleTheme()
      } else if (e.key === 'w' || e.key === 'W') {
        if (isDesktop) toggleWideMode()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goNext, goPrev, onClose, cycleTheme, isDesktop, toggleWideMode])

  // Render TOC items recursively
  const renderTocItems = (items: TocItem[], depth = 0) => {
    return items.map((item, index) => (
      <div key={`${depth}-${index}`}>
        <button
          onClick={() => goToTocItem(item.href)}
          className="toc-item block w-full text-left py-2 px-3 text-sm hover:bg-muted rounded transition-colors"
          style={{ paddingLeft: `${12 + depth * 16}px` }}
        >
          {item.label}
        </button>
        {item.subitems && item.subitems.length > 0 && renderTocItems(item.subitems, depth + 1)}
      </div>
    ))
  }

  if (error) {
    return (
      <div className={`foliate-viewer-error flex items-center justify-center h-full bg-muted ${className}`}>
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          {onClose && (
            <Button onClick={onClose} variant="outline">
              {t('common.close', 'Close')}
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`foliate-viewer relative h-full flex flex-col bg-background ${className}`}>
      {/* Loading overlay */}
      {loading && (
        <div className="loading-overlay absolute inset-0 flex items-center justify-center bg-background/80 z-50">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
            <p className="text-muted-foreground">{t('reader.loading', 'Loading book...')}</p>
          </div>
        </div>
      )}

      {/* Preview ended overlay - only show if no parent callback is handling it */}
      {previewEnded && !onPreviewEnd && (
        <div className="preview-ended-overlay absolute inset-0 flex items-center justify-center bg-background/90 z-50">
          <div className="text-center max-w-md p-6">
            <h3 className="text-xl font-semibold mb-2">{t('reader.previewEnded', 'Preview Ended')}</h3>
            <p className="text-muted-foreground mb-4">
              {t('reader.previewEndedDescription', 'You\'ve reached the end of the preview. Download the full book to continue reading.')}
            </p>
            {onClose && (
              <Button onClick={onClose}>
                {t('reader.backToBook', 'Back to Book')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Top bar */}
      {showControls && (
        <div 
          className="reader-topbar flex items-center justify-center border-b transition-colors duration-200"
          style={{ backgroundColor: THEMES[theme].bg, color: THEMES[theme].text, borderColor: `${THEMES[theme].text}20` }}
        >
          <div className={`reader-topbar-inner flex items-center gap-2 px-2 py-1.5 w-full ${
            isDesktop && !isWideMode ? 'max-w-3xl' : ''
          }`}>
          {/* Left: Contents + Theme + Wide mode icons */}
          <div className="reader-controls-left flex items-center gap-1 shrink-0">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 hover:bg-black/10"
              style={{ color: THEMES[theme].text }}
              onClick={() => setShowToc(!showToc)}
              title={t('reader.contents', 'Contents')}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8 hover:bg-black/10"
              style={{ color: THEMES[theme].text }}
              onClick={cycleTheme}
              title={`${t('reader.theme', 'Theme')}: ${THEMES[theme].name}`}
            >
              {theme === 'light' && <Sun className="h-4 w-4" />}
              {theme === 'dark' && <Moon className="h-4 w-4" />}
              {theme === 'sepia' && <Coffee className="h-4 w-4" />}
            </Button>
            {/* Font size settings */}
            <div className="settings-container relative">
              <Button 
                ref={settingsButtonRef}
                variant="ghost" 
                size="icon"
                className="h-8 w-8 hover:bg-black/10"
                style={{ color: THEMES[theme].text }}
                onClick={() => setShowSettings(!showSettings)}
                title={t('reader.settings', 'Settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
              {/* Settings popup */}
              {showSettings && (
                <div 
                  ref={settingsRef}
                  className="settings-popup absolute left-0 top-full mt-1 p-3 rounded-lg shadow-lg border z-50 min-w-[180px]"
                  style={{ 
                    backgroundColor: THEMES[theme].bg, 
                    borderColor: `${THEMES[theme].text}20`,
                    color: THEMES[theme].text
                  }}
                >
                  <div className="font-size-setting">
                    <div className="text-xs mb-2" style={{ opacity: 0.7 }}>
                      {t('reader.fontSize', 'Font Size')}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-black/10"
                        style={{ color: THEMES[theme].text }}
                        onClick={decreaseFontSize}
                        disabled={fontSize <= 80}
                        title={t('reader.decreaseFont', 'Decrease font size')}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-medium w-12 text-center">{fontSize}%</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-black/10"
                        style={{ color: THEMES[theme].text }}
                        onClick={increaseFontSize}
                        disabled={fontSize >= 150}
                        title={t('reader.increaseFont', 'Increase font size')}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Wide/Narrow mode toggle - desktop only */}
            {isDesktop && (
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 hover:bg-black/10"
                style={{ color: THEMES[theme].text }}
                onClick={toggleWideMode}
                title={isWideMode ? t('reader.narrowMode', 'Narrow mode') : t('reader.wideMode', 'Wide mode')}
              >
                {isWideMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            )}
          </div>

          {/* Center: Book title + Language + Level */}
          <div className="reader-meta flex-1 flex items-center justify-center gap-1.5 min-w-0 text-sm">
            <span className="reader-title font-medium truncate max-w-[180px] sm:max-w-[280px]">
              {bookTitle}
            </span>
            {bookLanguage && (
              <>
                <span style={{ opacity: 0.6 }}>•</span>
                <span className="reader-language shrink-0" style={{ opacity: 0.6 }}>{bookLanguage}</span>
              </>
            )}
            {bookLevel && (
              <>
                <span style={{ opacity: 0.6 }}>•</span>
                <span className="reader-level shrink-0" style={{ opacity: 0.6 }}>{bookLevel}</span>
              </>
            )}
          </div>

          {/* Right: Progress + Close */}
          <div className="reader-controls-right flex items-center gap-2 shrink-0">
            <span className="reader-progress text-xs" style={{ opacity: 0.6 }}>
              {Math.round(currentPercent)}%
            </span>
            {isPreview && (
              <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded hidden sm:inline">
                {t('reader.preview', 'Preview')}
              </span>
            )}
            {onClose && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 hover:bg-black/10"
                style={{ color: THEMES[theme].text }}
                onClick={onClose}
                title={t('common.close', 'Close')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="reader-content flex-1 relative overflow-hidden">
        {/* Table of contents sidebar */}
        {showToc && (
          <div className="toc-sidebar absolute left-0 top-0 bottom-0 w-80 bg-card border-r z-40 overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">{t('reader.tableOfContents', 'Table of Contents')}</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowToc(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <nav>
                {renderTocItems(toc)}
              </nav>
            </div>
          </div>
        )}

        {/* Foliate content - constrained width on desktop for better readability (narrow mode) */}
        <div 
          className="foliate-content-wrapper absolute inset-0 flex justify-center transition-colors duration-200"
          style={{ backgroundColor: THEMES[theme].bg }}
        >
          {/* In narrow mode on desktop, use a relative container for nav buttons */}
          <div className={`foliate-content-container relative flex items-stretch ${
            isDesktop && !isWideMode ? 'max-w-3xl' : ''
          } w-full h-full`}>
            {/* Navigation button - prev (positioned just outside content in narrow mode when there's space and not navAtEdges) */}
            {showNavigation && !loading && !navAtEdges && isDesktop && !isWideMode && hasSpaceForOutsideNav && (
              <Button
                variant="ghost"
                size="lg"
                className="nav-prev absolute left-0 -translate-x-full top-1/2 -translate-y-1/2 h-20 w-12 opacity-50 hover:opacity-100 z-10 hover:bg-black/10"
                style={{ color: THEMES[theme].text }}
                onClick={goPrev}
                disabled={isPreview && previewStartPercent > 0 && currentPercent <= previewStartPercent + 1}
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
            )}

            <div 
              ref={containerRef} 
              className="foliate-content w-full h-full"
              style={{ 
                opacity: loading ? 0 : 1,
                backgroundColor: THEMES[theme].bg
              }}
            />

            {/* Navigation button - next (positioned just outside content in narrow mode when there's space and not navAtEdges) */}
            {showNavigation && !loading && !navAtEdges && isDesktop && !isWideMode && hasSpaceForOutsideNav && (
              <Button
                variant="ghost"
                size="lg"
                className="nav-next absolute right-0 translate-x-full top-1/2 -translate-y-1/2 h-20 w-12 opacity-50 hover:opacity-100 z-10 hover:bg-black/10"
                style={{ color: THEMES[theme].text }}
                onClick={goNext}
                disabled={previewEnded}
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            )}
          </div>
        </div>

        {/* Navigation buttons - at screen edges for: navAtEdges mode, wide mode, mobile, or when not enough space for outside nav */}
        {showNavigation && !loading && (navAtEdges || isWideMode || !isDesktop || (isDesktop && !isWideMode && !hasSpaceForOutsideNav)) && (
          <>
            <Button
              variant="ghost"
              size="lg"
              className="nav-prev absolute left-2 top-1/2 -translate-y-1/2 h-20 w-12 opacity-50 hover:opacity-100 hover:bg-black/10"
              style={{ color: THEMES[theme].text }}
              onClick={goPrev}
              disabled={isPreview && previewStartPercent > 0 && currentPercent <= previewStartPercent + 1}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="nav-next absolute right-2 top-1/2 -translate-y-1/2 h-20 w-12 opacity-50 hover:opacity-100 hover:bg-black/10"
              style={{ color: THEMES[theme].text }}
              onClick={goNext}
              disabled={previewEnded}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </>
        )}
      </div>

    </div>
  )
}

